import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DistrictService } from '../../../shared/services/district.service';
import { ChurchService } from '../../../shared/services/church.service';
import { DistrictSurveyService } from '../../../shared/services/district_survey.service';
import { PeriodService } from '../../../periods/period.service';
import { ReportResponseService } from '../../services/report_response.service';
import { UserService } from '../../../user/services/user.service';
import Swal from 'sweetalert2';

// Estados desde los que el informe del periodo aún no ha sido enviado (mismos IDs usados en el backend)
const SENDABLE_STATES = ['67bf30fae343839c29db3a3f', '67bf37c8e343839c29db3a47'];

@Component({
  selector: 'app-district-data',
  templateUrl: './district-data.component.html',
  styleUrls: ['./district-data.component.scss']
})
export class DistrictDataComponent implements OnInit {
  districtDataForm!: FormGroup;

  districtOptions: { id: string; label: string }[] = [];
  churchOptions: { id: string; label: string }[] = [];
  periodOptions: { id: string; label: string }[] = [];

  surveyResults: any[] = [];

  isEdit: boolean = false;
  editingSurveyId: string = '';
  canEditSurvey: boolean = true;

  checkingDistrict: boolean = true;
  hasDistrict: boolean = true;
  userDistrictId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private districtService: DistrictService,
    private churchService: ChurchService,
    private districtSurveyService: DistrictSurveyService,
    private periodService: PeriodService,
    private reportResponseService: ReportResponseService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.districtDataForm = this.fb.group({
      periodo: [null, Validators.required],
      distrito: [null, Validators.required],
      iglesia: [null, Validators.required],
      cierreRemesasAlDia: ['No', Validators.required],
      votoInformeTesoreria: ['', Validators.required],
      fichasBautismosPendientes: [null, [Validators.required, Validators.min(0)]],
      participantesIdentidad: [null, [Validators.required, Validators.min(0)]],
      participantesCreciendoEnCristo: [null, [Validators.required, Validators.min(0)]],
      lideresProgramasLiderazgo: [null, [Validators.required, Validators.min(0)]],
      participantesEscuelaMisionera: [null, [Validators.required, Validators.min(0)]],
      personasEstudioBiblicos: [null, [Validators.required, Validators.min(0)]],
      estudiantesBiblia: [null, [Validators.required, Validators.min(0)]],
      bautismosMes: [null, [Validators.required, Validators.min(0)]],
      bautismosAcumulado: [null, [Validators.required, Validators.min(0)]],
    });

    this.loadPeriods();
    this.checkUserDistrict();

    this.districtDataForm.get('distrito')!.valueChanges.subscribe((districtId: string) => {
      this.districtDataForm.get('iglesia')!.setValue(null);
      this.loadChurches(districtId);
    });

    this.districtDataForm.get('iglesia')!.valueChanges.subscribe(() => {
      this.refreshFormForSelection();
    });

    this.districtDataForm.get('periodo')!.valueChanges.subscribe((periodId: string) => {
      this.loadSurveyResults(periodId);
      this.checkEditPermission(periodId);
    });
  }

  checkUserDistrict(): void {
    if (this.userService.getUserRole() !== 'Usuario') {
      this.checkingDistrict = false;
      this.hasDistrict = true;
      this.loadDistricts();
      return;
    }

    const userId = this.userService.getselectedUserId();
    this.districtService.getByPastor(userId).subscribe({
      next: (districts: any[]) => {
        if (districts && districts.length > 0) {
          this.hasDistrict = true;
          this.userDistrictId = districts[0]._id;
          this.districtOptions = [{ id: districts[0]._id, label: districts[0].name }];
          this.districtDataForm.get('distrito')!.setValue(districts[0]._id);
        } else {
          this.hasDistrict = false;
        }
        this.checkingDistrict = false;
      },
      error: (error) => {
        console.error('Error al verificar el distrito asignado:', error);
        this.hasDistrict = false;
        this.checkingDistrict = false;
      }
    });
  }

  checkEditPermission(periodId: string): void {
    if (!periodId) {
      this.canEditSurvey = true;
      return;
    }

    if (this.userService.getUserRole() !== 'Usuario') {
      this.canEditSurvey = true;
      return;
    }

    const userId = this.userService.getselectedUserId();
    this.reportResponseService.get(periodId, userId).subscribe({
      next: (reports: any[]) => {
        this.canEditSurvey = !reports || reports.length === 0 || SENDABLE_STATES.includes(reports[0].stateId);
      },
      error: (error) => {
        console.error('Error al verificar el estado del informe:', error);
        this.canEditSurvey = true;
      }
    });
  }

  refreshFormForSelection(): void {
    const periodoId = this.districtDataForm.get('periodo')!.value;
    const iglesiaId = this.districtDataForm.get('iglesia')!.value;
    if (!periodoId || !iglesiaId) {
      return;
    }

    const existing = this.surveyResults.find(r => r.periodoId === periodoId && r.iglesiaId === iglesiaId);
    if (existing) {
      this.editSurvey(existing);
    } else {
      this.cancelEdit();
      this.resetAnswers();
    }
  }

  loadPeriods(): void {
    this.periodService.get().subscribe({
      next: (periods: any[]) => {
        const sortedPeriods = [...periods].sort((a, b) => (b.year - a.year) || (b.month - a.month));
        this.periodOptions = sortedPeriods.map(p => ({ id: p._id, label: p.name }));
        if (this.periodOptions.length) {
          this.districtDataForm.get('periodo')!.setValue(this.periodOptions[0].id);
        }
      },
      error: (error) => {
        console.error('Error al cargar los periodos:', error);
        Swal.fire('Error', 'No se pudieron cargar los periodos.', 'error');
      }
    });
  }

  loadSurveyResults(periodId?: string): void {
    const obs = this.userDistrictId
      ? this.districtSurveyService.getFiltered({ periodId, districtId: this.userDistrictId })
      : this.districtSurveyService.get(periodId);

    obs.subscribe({
      next: (surveys: any[]) => {
        this.surveyResults = surveys.map(s => ({
          _id: s._id,
          periodoId: s.period?._id,
          distritoId: s.district?._id,
          iglesiaId: s.church?._id,
          periodo: s.period?.name,
          distrito: s.district?.name,
          iglesia: s.church?.name,
          cierreRemesasAlDia: s.cierreRemesasAlDia,
          votoInformeTesoreria: s.votoInformeTesoreria,
          fichasBautismosPendientes: s.fichasBautismosPendientes,
          participantesIdentidad: s.participantesIdentidad,
          participantesCreciendoEnCristo: s.participantesCreciendoEnCristo,
          lideresProgramasLiderazgo: s.lideresProgramasLiderazgo,
          participantesEscuelaMisionera: s.participantesEscuelaMisionera,
          personasEstudioBiblicos: s.personasEstudioBiblicos,
          estudiantesBiblia: s.estudiantesBiblia,
          bautismosMes: s.bautismosMes,
          bautismosAcumulado: s.bautismosAcumulado
        }));
        this.refreshFormForSelection();
      },
      error: (error) => {
        console.error('Error al cargar los resultados de encuestas:', error);
        Swal.fire('Error', 'No se pudieron cargar los resultados de encuestas.', 'error');
      }
    });
  }

  loadDistricts(): void {
    this.districtService.get().subscribe({
      next: (districts: any[]) => {
        this.districtOptions = districts.map(d => ({ id: d._id, label: d.name }));
        if (this.districtOptions.length) {
          this.districtDataForm.get('distrito')!.setValue(this.districtOptions[0].id);
        }
      },
      error: (error) => {
        console.error('Error al cargar los distritos:', error);
        Swal.fire('Error', 'No se pudieron cargar los distritos.', 'error');
      }
    });
  }

  loadChurches(districtId: string, churchIdToSelect?: string): void {
    if (!districtId) {
      this.churchOptions = [];
      return;
    }
    this.churchService.get(districtId).subscribe({
      next: (churches: any[]) => {
        this.churchOptions = churches.map(c => ({ id: c._id, label: c.name }));
        if (churchIdToSelect) {
          this.districtDataForm.get('iglesia')!.setValue(churchIdToSelect, { emitEvent: false });
        } else if (this.churchOptions.length) {
          this.districtDataForm.get('iglesia')!.setValue(this.churchOptions[0].id);
        }
      },
      error: (error) => {
        console.error('Error al cargar las iglesias:', error);
        Swal.fire('Error', 'No se pudieron cargar las iglesias.', 'error');
      }
    });
  }

  saveDistrictData(): void {
    if (!this.canEditSurvey) {
      Swal.fire('No permitido', 'No puedes modificar la encuesta distrital una vez enviado el informe del periodo.', 'warning');
      return;
    }

    if (this.districtDataForm.invalid) {
      this.districtDataForm.markAllAsTouched();
      return;
    }

    const { periodo, distrito, iglesia, ...surveyAnswers } = this.districtDataForm.value;
    const surveyData = { period: periodo, district: distrito, church: iglesia, ...surveyAnswers };

    const operation = this.isEdit
      ? this.districtSurveyService.update(this.editingSurveyId, surveyData)
      : this.districtSurveyService.save(surveyData);

    operation.subscribe({
      next: () => {
        Swal.fire('Éxito', this.isEdit ? 'Datos distritales actualizados correctamente.' : 'Datos distritales guardados correctamente.', 'success');
        this.cancelEdit();
        this.resetAnswers();
        this.loadSurveyResults(periodo);
      },
      error: (error) => {
        console.error('Error al guardar los datos distritales:', error);
        const message = error?.error?.message || 'No se pudieron guardar los datos distritales.';
        Swal.fire('Error', message, 'error');
      }
    });
  }

  editSurvey(survey: any): void {
    this.isEdit = true;
    this.editingSurveyId = survey._id;

    this.districtDataForm.patchValue({
      periodo: survey.periodoId,
      distrito: survey.distritoId,
      cierreRemesasAlDia: survey.cierreRemesasAlDia,
      votoInformeTesoreria: survey.votoInformeTesoreria,
      fichasBautismosPendientes: survey.fichasBautismosPendientes,
      participantesIdentidad: survey.participantesIdentidad,
      participantesCreciendoEnCristo: survey.participantesCreciendoEnCristo,
      lideresProgramasLiderazgo: survey.lideresProgramasLiderazgo,
      participantesEscuelaMisionera: survey.participantesEscuelaMisionera,
      personasEstudioBiblicos: survey.personasEstudioBiblicos,
      estudiantesBiblia: survey.estudiantesBiblia,
      bautismosMes: survey.bautismosMes,
      bautismosAcumulado: survey.bautismosAcumulado,
    }, { emitEvent: false });

    this.loadChurches(survey.distritoId, survey.iglesiaId);
  }

  cancelEdit(): void {
    this.isEdit = false;
    this.editingSurveyId = '';
  }

  resetAnswers(): void {
    this.districtDataForm.patchValue({
      cierreRemesasAlDia: 'No',
      votoInformeTesoreria: '',
      fichasBautismosPendientes: null,
      participantesIdentidad: null,
      participantesCreciendoEnCristo: null,
      lideresProgramasLiderazgo: null,
      participantesEscuelaMisionera: null,
      personasEstudioBiblicos: null,
      estudiantesBiblia: null,
      bautismosMes: null,
      bautismosAcumulado: null,
    });
    this.districtDataForm.markAsPristine();
    this.districtDataForm.markAsUntouched();
  }
}
